import type { ShippingOptionContentVariantKeys } from '@/components/order/shippingOptions/ShippingOptionCard/types';
import { getContent } from '@/components/order/shippingOptions/ShippingOptionCard/utils';
import { calculateDiscountedPrice } from '@/components/order/utils';
import { Logger } from '@/utils/logger';
import { DELIVERY_OPTION, DELIVERY_OPTIONS, getAdditionalTemporalInfo } from '@montugroup/circuit-shared';
import type { ArrayElement } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { Typography } from '@montugroup/design-system';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import type { DeliveryOptions } from './types';
import type useDeliveryOptions from './useDeliveryOptions';

const logger = new Logger('useStandardDeliveryOption');

const ESTIMATED_ARRIVAL_TIME = '1-3 business days';

// TODO: CONTENT_VARIANTS to be sourced from LaunchDarkly
const CONTENT_VARIANTS: ShippingOptionContentVariantKeys = {
  title: 'A',
  description: 'A',
  icon: 'A',
};

const CONTENT = {
  icons: { A: <LocalShippingOutlinedIcon /> },
  titles: { A: DELIVERY_OPTIONS[DELIVERY_OPTION.Standard].displayName },
  descriptions: {
    A: () => {
      return <Typography>Arrives in 1-3 business days.</Typography>;
    },
  },
} as const;

const getSettings = (option?: ArrayElement<ReturnType<typeof useDeliveryOptions>['deliveryOptions']['Standard']>) => {
  if (!option) {
    return null;
  }

  const discountedPrice = calculateDiscountedPrice({
    price: option.shippingPrice,
    discountPercent: option.discount?.percentage,
  });

  const content = getContent<typeof CONTENT>({
    content: CONTENT,
    variants: CONTENT_VARIANTS,
    // TODO: Figure out how to not need to pass anything for descriptionArgs if the description function doesn't have any required args
    descriptionArgs: undefined,
  });

  const extraDetails = {
    estimatedArrivalTime: ESTIMATED_ARRIVAL_TIME,
    discountPercentage: option.discount?.percentage,
    discountedPrice,
  };

  return {
    ...option,
    ...content,
    ...extraDetails,
  };
};

export default function useStandardDeliveryOption(props: DeliveryOptions['Standard']) {
  const settings = useMemo(() => getSettings(props[0]), [props]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { icon, description, ...loggableSettings } = settings ?? {};

    const additionalTemporalLogs = {
      browserTime: {
        ...getAdditionalTemporalInfo(DateTime.now().toISO()),
      },
      validBefore: {
        ...getAdditionalTemporalInfo(settings?.validBefore),
      },
    };

    logger.info('settings', {
      ...loggableSettings,
      shouldShow: Boolean(settings),
      additionalTemporalLogs,
      props,
    });
  }, [settings, props]);

  return settings;
}
